export function isUploadFailedDoc(docData) {
  const u = typeof docData?.upload === "string" ? docData.upload : null;
  const s = typeof docData?.status === "string" ? docData.status : null;

  // NEW canonical upload field
  if (u === "failed") return true;

  // Legacy fallback
  if (s === "error" || s === "failed") return true;

  return false;
}

function normalizeUploadDiagnostics(docData) {
  const diagnostics = docData?.uploadDiagnostics;
  return diagnostics && typeof diagnostics === "object" ? diagnostics : null;
}

function readUploadEvents(diagnostics) {
  return Array.isArray(diagnostics?.events) ? diagnostics.events : [];
}

function hasUploadStage(diagnostics, stage) {
  return readUploadEvents(diagnostics).some((event) => event?.stage === stage);
}

function findUploadStage(diagnostics, stage) {
  return readUploadEvents(diagnostics).find((event) => event?.stage === stage);
}

function hasUploadStageDetail(diagnostics, stage, key, value) {
  return readUploadEvents(diagnostics).some(
    (event) =>
      event?.stage === stage && event?.details?.[key] === value,
  );
}

function readNumericDetail(event, key) {
  const value = event?.details?.[key];
  return Number.isFinite(value) ? value : null;
}

export function getUploadFailedMessage(docData) {
  // recordIndex.js writes { error: { message, code } } in finalizeRecordingIndexError
  const msg =
    docData?.error?.message ||
    docData?.errorMessage ||
    docData?.uploadError ||
    null;

  const code = docData?.error?.code || null;

  return { msg, code };
}

export function getUploadFailedUserMessage(docData) {
  const diagnostics = normalizeUploadDiagnostics(docData);
  const technical = getUploadFailedMessage(docData);

  if (!diagnostics) {
    return null;
  }

  const technicalText = `${technical?.code || ""} ${technical?.msg || ""}`.trim();
  const currentOnline = diagnostics?.client?.online;
  const offlineSeen =
    diagnostics?.offlineEventCount > 0 ||
    currentOnline === false ||
    hasUploadStage(diagnostics, "network:offline") ||
    /network-request-failed|failed to fetch|network error|offline/i.test(
      technicalText,
    );

  if (offlineSeen) {
    return {
      bodyKey: "result.uploadFailedConnectionBody",
      bodyDefault:
        "Your connection dropped while your recording was uploading.",
      hintKey: "result.uploadFailedConnectionHint",
      hintDefault: "Reconnect to the internet and try again.",
      showTechnicalMessage: false,
    };
  }

  const leftPageDuringUpload =
    hasUploadStage(diagnostics, "page:beforeunload") ||
    hasUploadStage(diagnostics, "page:hide") ||
    hasUploadStageDetail(diagnostics, "uploadVideo:state", "state", "paused") ||
    diagnostics?.hiddenEventCount > 0;

  if (leftPageDuringUpload) {
    return {
      bodyKey: "result.uploadFailedBackgroundBody",
      bodyDefault:
        "The upload was interrupted because this page was closed or moved into the background.",
      hintKey: "result.uploadFailedBackgroundHint",
      hintDefault: "Keep the app open until the upload finishes, then try again.",
      showTechnicalMessage: false,
    };
  }

  const retainedVideoEvent = findUploadStage(diagnostics, "videoRetention:done");
  const retainedSizeBytes =
    readNumericDetail(retainedVideoEvent, "sizeBytes") ??
    (Number.isFinite(diagnostics?.initial?.sizeBytes)
      ? diagnostics.initial.sizeBytes
      : null);
  const uploadStarted =
    hasUploadStage(diagnostics, "uploadVideo:start") ||
    hasUploadStage(diagnostics, "uploadVideo:progress") ||
    hasUploadStage(diagnostics, "uploadVideo:done");
  const recordingTooShort =
    (Number.isFinite(retainedSizeBytes) && retainedSizeBytes < 150000) ||
    (Number.isFinite(diagnostics?.durationMs) &&
      diagnostics.durationMs < 8000 &&
      !uploadStarted);

  if (recordingTooShort) {
    return {
      bodyKey: "result.uploadFailedShortRecordingBody",
      bodyDefault:
        "This recording ended before your device could save a complete video.",
      hintKey: "result.uploadFailedShortRecordingHint",
      hintDefault:
        "Please record again and keep the app open until the recording finishes.",
      showTechnicalMessage: false,
    };
  }

  return null;
}
